import Button from "@components/ui/button";
import DialogBase from "@components/ui/dialog-base";
import { ScrollArea } from "@components/ui/scroll-area";
import { IconArrowRight } from "@tabler/icons-react";
import { useEffect, useRef } from "react";
import {
  MAX_SELECTED_PAGES,
  selectSelectedPageNumbers,
  useFileToStorydocStoreShallow,
} from "../stores";
import {
  selectViewing,
  useImportRegistry,
} from "../stores/import-registry.store";
import { isSameImportRequest } from "../utils/is-same-import-request";
import { trackImportPreJob } from "../utils/track-import";
import { ImportDialogFooter } from "./import-dialog-footer";
import { PageThumbnailGrid } from "./page-thumbnail-grid";
import { ThemeSelector } from "./theme-selector";

export const SelectPagesStep = ({ onContinue }: { onContinue: () => void }) => {
  const {
    pages,
    pdfUrl,
    selectedDesignSystemId,
    setAllSelected,
    returnedFromVersions,
  } = useFileToStorydocStoreShallow((s) => ({
    pages: s.pages,
    pdfUrl: s.pdfUrl,
    returnedFromVersions: s.returnedFromVersions,
    selectedDesignSystemId: s.selectedDesignSystemId,
    setAllSelected: s.setAllSelected,
  }));
  const viewing = useImportRegistry(selectViewing)?.store.getState();
  const selectedPages = selectSelectedPageNumbers(pages);
  const selectedCount = selectedPages.length;
  const overCap = pages.length > MAX_SELECTED_PAGES;
  const atCap = overCap && selectedCount >= MAX_SELECTED_PAGES;
  const allSelected =
    pages.length > 0 &&
    selectedCount === Math.min(pages.length, MAX_SELECTED_PAGES);
  const willRegenerate =
    returnedFromVersions &&
    viewing?.request?.pdfUrl === pdfUrl &&
    !isSameImportRequest(
      { pdfUrl, pages: selectedPages, selectedDesignSystemId },
      viewing,
    );
  const overLimitTrackedRef = useRef(false);

  useEffect(() => {
    if (!atCap || overLimitTrackedRef.current) return;
    overLimitTrackedRef.current = true;
    trackImportPreJob("over limit shown", { pages: pages.length });
  }, [atCap, pages.length]);

  const handleBulk = () => {
    setAllSelected(!allSelected);
    trackImportPreJob("page toggled", { bulk: true, selected: !allSelected });
  };

  return (
    <DialogBase.Content className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-p-0">
      <ScrollArea fill className="-tw-mb-6 tw-min-h-0 tw-flex-1">
        <div className="tw-mx-auto tw-flex tw-w-[688px] tw-max-w-full tw-flex-col tw-gap-10 tw-px-0.5 tw-pb-28 tw-pt-0.5">
          <div className="tw-rounded-xl tw-bg-white tw-p-4 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5">
            <ThemeSelector />
          </div>
          <section className="tw-flex tw-flex-col tw-gap-6">
            <div className="tw-flex tw-items-center tw-justify-between">
              <h3 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-blue">
                Pages
              </h3>
              <button
                type="button"
                onClick={handleBulk}
                className="tw-flex tw-h-8 tw-items-center tw-rounded-lg tw-px-3 tw-text-xs tw-font-medium tw-text-blue tw-transition-colors tw-duration-200 hover:tw-bg-blue-50"
              >
                {allSelected ? "Deselect all" : "Select all"}
              </button>
            </div>
            <PageThumbnailGrid atCap={atCap} />
          </section>
        </div>
      </ScrollArea>
      <ImportDialogFooter>
        {atCap ? (
          <div className="tw-flex tw-flex-col">
            <span className="tw-text-base tw-font-medium tw-text-blue">
              Your file has more than 30 slides
            </span>
            <span className="tw-text-sm tw-text-blue-500">
              Only the first 30 slides will be imported.
            </span>
          </div>
        ) : (
          <span className="tw-text-base tw-font-medium tw-text-blue">{`${selectedCount} ${selectedCount === 1 ? "slide" : "slides"} to be imported`}</span>
        )}
        <div className="tw-flex tw-items-center tw-gap-3">
          {willRegenerate && (
            <p className="tw-m-0 tw-text-xs tw-text-blue-500">
              This will regenerate all slides.
            </p>
          )}
          <Button
            onClick={onContinue}
            disabled={selectedCount === 0}
            className="tw-group"
          >
            Continue
            <IconArrowRight className="tw-ml-1.5 tw-size-4 !tw-fill-none tw-text-white group-disabled:tw-text-blue-700" />
          </Button>
        </div>
      </ImportDialogFooter>
    </DialogBase.Content>
  );
};
